import * as React from 'react';
import {
  DataGrid,
  GridColDef,
  GridRowModesModel,
  GridSlotsComponentsProps,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { LineItemInput, TaxType } from '@admiin-com/ds-graphql';
import { WBFlex, WBIconButton, WBLinkButton } from '@admiin-com/ds-web';
import { useTheme } from '@mui/material';
import { GridRowModel } from '@mui/x-data-grid';
import { GridRowsProp } from '@mui/x-data-grid';
import { numberToCurrency } from '@admiin-com/ds-common';
import { useSingleClickTableParams } from '../../InvoiceLineItems/useSingleClickTableParams';
import { ServicesLookupEditCell } from '../../ServicesLookup/ServicesLookupEditCell';
import CreateServiceModal from '../../../pages/ServiceCreateModal';

interface Props {
  setRows: (rows: LineItemInput[]) => void;
  rows: LineItemInput[];
  defaultGST?: TaxType;
  hasServiceLookup?: boolean;
  fromEntityId?: string;
  noGstInclusive?: boolean;
}

export default function LineItemInputTable({
  defaultGST = TaxType.NO_GST,
  hasServiceLookup = false,
  fromEntityId,
  noGstInclusive,
  ...props
}: Props) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const [editedCellRowId, setEditedCellRowId] = React.useState<
    GridRowModel['id'] | null
  >(null);
  const columns: GridColDef<(typeof initialRows)[number]>[] = [
    {
      field: 'id',
      headerName: '#',
      flex: 0.1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'description',
      headerName: t('description', { ns: 'taskbox' }),
      flex: 0.5,
      disableColumnMenu: true,
      sortable: false,
      editable: true,
      renderCell: (params) => {
        // Show placeholder when cell is empty
        if (!params.value) {
          return (
            <span style={{ color: theme.palette.text.disabled }}>
              {t('searchOrEnterService', {
                ns: 'taskbox',
                defaultValue: 'Search or enter a new service',
              })}
            </span>
          );
        }
        return params.value;
      },
      ...(hasServiceLookup && fromEntityId
        ? {
            renderEditCell: (params) => {
              return (
                <ServicesLookupEditCell
                  onCreateNew={(id) => {
                    console.log('Creating new service', id);
                    setEditedCellRowId(id);
                    setModalOpen(true);
                  }}
                  entityId={fromEntityId}
                  {...params}
                  value={params.value}
                  placeholder={t('searchOrEnterService', {
                    ns: 'taskbox',
                    defaultValue: 'Search or enter a new service',
                  })}
                  onChange={(value) => {
                    console.log(
                      'Changing value',
                      params,
                      value,
                      editedCellRowId
                    );
                    if (value.id) {
                      apiRef.current.updateRows([
                        {
                          id: params.id,
                          description: value?.name || '',
                          qty: 1,
                          price: value?.amount || 0,
                          amount: value?.amount || 0,
                          taxType: value?.taxType || '',
                          serviceId: value?.id || '',
                          newService: false,
                        },
                      ]);
                    } else {
                      if (editedCellRowId) {
                        setEditedCellRowId(null);
                        return;
                      }
                      apiRef.current.updateRows([
                        {
                          ...params,
                          newService: true,
                          serviceId: '',
                        },
                      ]);
                    }
                  }}
                />
              );
            },
          }
        : {
            renderEditCell: (params) => {
              return (
                <input
                  className="MuiInputBase-input"
                  style={{
                    width: '100%',
                    border: 'none',
                    height: '100%',
                    padding: '0 16px',
                    background: 'none',
                    color: theme.palette.text.primary,
                  }}
                  value={params.value}
                  onChange={(e) => {
                    params.api.setEditCellValue({
                      id: params.id,
                      field: params.field,
                      value: e.target.value,
                    });
                  }}
                  placeholder={t('searchOrEnterService', {
                    ns: 'taskbox',
                    defaultValue: 'Search or enter a new service',
                  })}
                  autoFocus
                />
              );
            },
          }),
    },
    {
      field: 'qty',
      headerName: t('qty', { ns: 'taskbox' }),
      type: 'number',
      sortable: false,
      flex: 0.1,
      disableColumnMenu: true,
      editable: true,
    },
    {
      field: 'price',
      headerName: t('price', { ns: 'taskbox' }),
      type: 'number',
      flex: 0.3,
      editable: true,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (value) => numberToCurrency(value),
    },
    {
      field: 'taxType',
      headerName: t('GST', { ns: 'taskbox' }),
      sortable: false,
      type: 'singleSelect',
      editable: true,
      align: 'right',
      headerAlign: 'right',
      flex: 0.2,
      disableColumnMenu: true,
      valueOptions: Object.values(TaxType)
        .map((value) => ({
          value,
          label: t(value, { ns: 'common' }),
        }))
        .filter(
          (value) => !noGstInclusive || value.value !== TaxType.GST_INCLUSIVE
        ),
    },
    {
      field: 'amount',
      headerName: t('amount', { ns: 'taskbox' }),
      sortable: false,
      editable: true,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      flex: 0.3,
      valueFormatter: (value) => numberToCurrency(value),
    },
    {
      field: 'actions',
      headerName: '',
      disableColumnMenu: true,
      flex: 0.2,
      editable: false,
      sortable: false,
      renderCell: (params) => (
        <WBIconButton
          onClick={() => handleDelete(params.id as number)}
          icon="Trash"
          size="small"
          sx={{ display: 'none' }}
          className="delete-icon"
        />
      ),
    },
  ];
  const handleDelete = (id: number) => {
    const newRows = rows.filter((row) => row.id !== id);
    updatePropsRows(newRows);
    apiRef.current.updateRows([{ id, _action: 'delete' }]);
  };
  const initialRows: GridRowsProp = [
    { id: 1, description: '', qty: 1, price: 0, taxType: defaultGST },
  ];
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleAddNew = () => {
    const newRow = {
      id: rows.length + 1,
      description: '',
      qty: 1,
      price: 0,
      amount: 0,
      taxType: defaultGST,
      newService: true,
    };
    const newRows = [...rows, newRow];
    updatePropsRows(newRows);
    setRows(newRows); // Ensure rows state is updated before interacting with the API

    // Safely set cell focus after rows are updated
    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.setCellFocus(newRow.id, 'description');
      }
    }, 0);
  };

  const [rows, setRows] = React.useState<GridRowsProp>([]);

  const processRowUpdate = (newRow: GridRowModel) => {
    const existingRow = rows.find((row) => row.id === newRow.id);
    if (!existingRow) return newRow;

    let { price, qty, amount } = newRow;
    const { taxType } = newRow;

    qty = qty <= 0 ? 1 : qty;

    if (newRow.price !== existingRow.price || newRow.qty !== existingRow.qty) {
      amount =
        Math.round(price * qty * (taxType === TaxType.GST ? 1.1 : 1) * 100) /
        100;
    } else if (newRow.amount !== existingRow.amount) {
      price =
        Math.round((amount / qty / (taxType === TaxType.GST ? 1.1 : 1)) * 100) /
        100;
      amount = Math.round(amount * 100) / 100;
    } else if (newRow.taxType !== existingRow.taxType) {
      // Recalculate amount when GST option changes
      amount =
        Math.round(price * qty * (taxType === TaxType.GST ? 1.1 : 1) * 100) /
        100;
    }

    const updatedRow = { ...newRow, price, qty, amount, isNew: false };
    const newRows = rows.map((row) =>
      row.id === newRow.id ? updatedRow : row
    );

    updatePropsRows(newRows);

    return updatedRow;
  };

  const updatePropsRows = (rows: GridRowModel[]) => {
    props.setRows(
      rows.map((row) => ({
        description: row.description,
        quantity: row.qty,
        unitAmount: row.price,
        lineAmount: row.amount,
        taxType: row.taxType ?? defaultGST,
        newService: row.newService,
        serviceId: row.serviceId,
      }))
    );
  };

  React.useEffect(() => {
    const newRows = props.rows.map((row, index) => ({
      description: row.description,
      price: row.unitAmount,
      amount: row.lineAmount,
      taxType: row.taxType ?? defaultGST,
      qty: row.quantity,
      id: index + 1,
      serviceId: row.serviceId,
      newService: row.newService,
    }));
    setRows(newRows);
  }, [defaultGST, props.rows]);

  const theme = useTheme();
  const singleClickTableParams = useSingleClickTableParams();

  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const handleCloseModal = () => {
    console.log('close modal');
    setModalOpen(false);
    setEditedCellRowId(null);
  };

  return (
    <>
      <DataGrid
        {...singleClickTableParams}
        apiRef={apiRef}
        sx={{
          borderWidth: 0,
          '.MuiDataGrid-row:hover .delete-icon': {
            display: 'inline-flex',
          },
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.background.default,
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-virtualScroller': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
        rows={rows}
        columns={columns}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        processRowUpdate={processRowUpdate}
        slots={{
          footer: AddNewButton,
        }}
        slotProps={{
          footer: {
            onAddNew: handleAddNew,
          },
        }}
        onCellKeyDown={(params, event) => {
          if (event.key === 'Enter') {
            event.preventDefault(); // Prevents default Enter behavior
            // event.stopPropagation(); // Stops the event from bubbling up
            // apiRef.current.stopEditing(); // Stops editing and saves changes
          }

          // Handle tab navigation between editable columns
          if (event.key === 'Tab') {
            event.preventDefault();
            const columnOrder = [
              'description',
              'qty',
              'price',
              'taxType',
              'amount',
            ];
            const currentColIndex = columnOrder.indexOf(params.field);
            const nextColIndex = !event.shiftKey
              ? (currentColIndex + 1) % columnOrder.length
              : (currentColIndex - 1 + columnOrder.length) % columnOrder.length;
            const nextField = columnOrder[nextColIndex];

            setTimeout(() => {
              apiRef.current.setCellFocus(params.id, nextField);
              apiRef.current.startCellEditMode({
                id: params.id,
                field: nextField,
              });
            }, 0);
          }
        }}
        disableRowSelectionOnClick
        showCellVerticalBorder={false}
      />

      <CreateServiceModal
        defaultZIndex={false}
        open={modalOpen}
        onClose={handleCloseModal}
        onSuccess={(value) => {
          console.log('SUccess', value);
          const updatetingRow = {
            id: editedCellRowId,
            description: value?.description || '',
            qty: 1,
            price: (value?.amount || 0) / 100,
            amount: (value?.amount || 0) / 100,
            taxType: value?.taxType || defaultGST,
            serviceId: value?.id || '',
            newService: true,
          };
          apiRef.current.updateRows([updatetingRow]);
          const newRows = rows.map((row) =>
            row.id === editedCellRowId
              ? {
                  ...row,
                  ...updatetingRow,
                }
              : row
          );
          updatePropsRows(newRows);
        }}
      />
    </>
  );
}

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    onAddNew: () => void;
  }
}

function AddNewButton(props: NonNullable<GridSlotsComponentsProps['footer']>) {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <WBFlex mb={4} mt={2} ml={2}>
      <WBLinkButton
        onClick={props.onAddNew}
        color="primary.main"
        sx={{ ...theme.typography.button }}
      >
        {t('addNewItem', { ns: 'taskbox' })}
      </WBLinkButton>
    </WBFlex>
  );
}
